// ════════════════════════════════════════════════════════════════════
// Payment Service — Stripe + PIX integration
// Calls edge functions to create payments and retrieve status
// ════════════════════════════════════════════════════════════════════

import { supabase } from "../supabaseClient.js";

export interface CreatePaymentResponse {
  success?: boolean;
  error?: string;
  payment_url?: string;
  pix_qr_code?: string;
  pix_copia_cola?: string;
}

export interface PaymentStatus {
  status: string;
  payment_method: string | null;
  receipt_url: string | null;
  payment_url?: string;
  pix_qr_code?: string;
  pix_copia_cola?: string;
}

/**
 * Create a payment for a cobranca
 * @param cobrancaId UUID of the cobranca
 * @param method 'stripe_card' | 'stripe_boleto' | 'pix'
 * @returns Payment details (URLs or QR code)
 */
export async function createPayment(
  cobrancaId: string,
  method: "stripe_card" | "stripe_boleto" | "pix"
): Promise<CreatePaymentResponse> {
  try {
    const { data, error } = await supabase.functions.invoke("create-payment", {
      body: {
        cobranca_id: cobrancaId,
        method,
      },
    });

    if (error) {
      console.error("create-payment error:", error);
      return { error: error.message || "Failed to create payment" };
    }

    return data || { error: "No response from payment service" };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    console.error("createPayment exception:", message);
    return { error: message };
  }
}

/**
 * Get payment status for a cobranca
 * @param cobrancaId UUID of the cobranca
 * @returns Current payment status and details
 */
export async function getPaymentStatus(cobrancaId: string): Promise<PaymentStatus> {
  try {
    const { data, error } = await supabase
      .from("cobrancas")
      .select("status, payment_method, receipt_url, payment_url, pix_qr_code, pix_copia_cola")
      .eq("id", cobrancaId)
      .single();

    if (error) {
      console.error("getPaymentStatus error:", error);
      return {
        status: "unknown",
        payment_method: null,
        receipt_url: null,
      };
    }

    return {
      status: data?.status || "pendente",
      payment_method: data?.payment_method || null,
      receipt_url: data?.receipt_url || null,
      payment_url: data?.payment_url || undefined,
      pix_qr_code: data?.pix_qr_code || undefined,
      pix_copia_cola: data?.pix_copia_cola || undefined,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    console.error("getPaymentStatus exception:", message);
    return {
      status: "unknown",
      payment_method: null,
      receipt_url: null,
    };
  }
}

/**
 * Get list of payment transactions for a cobranca
 * @param cobrancaId UUID of the cobranca
 * @returns Array of payment transaction records
 */
/**
 * Create or retrieve a Stripe Customer for a client
 * Returns stripe_customer_id + setup_intent_client_secret for payment method setup
 */
export async function createStripeCustomer(clientId: string): Promise<{
  stripe_customer_id?: string;
  setup_intent_client_secret?: string;
  already_exists?: boolean;
  error?: string;
}> {
  try {
    const { data, error } = await supabase.functions.invoke("create-stripe-customer", {
      body: { client_id: clientId },
    });

    if (error) {
      console.error("create-stripe-customer error:", error);
      return { error: error.message || "Failed to create Stripe customer" };
    }

    return data || { error: "No response" };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    console.error("createStripeCustomer exception:", message);
    return { error: message };
  }
}

export async function getPaymentTransactions(cobrancaId: string): Promise<
  Array<{
    id: string;
    status: string;
    provider: string;
    amount_cents: number;
    created_at: string;
  }>
> {
  try {
    const { data, error } = await supabase
      .from("payment_transactions")
      .select("id, status, provider, amount_cents, created_at")
      .eq("cobranca_id", cobrancaId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("getPaymentTransactions error:", error);
      return [];
    }

    return data || [];
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    console.error("getPaymentTransactions exception:", message);
    return [];
  }
}
